import { useQueryClient } from '@tanstack/react-query';
import { tsr } from '../../../lib/api.js';
import { useAuthenticatedSession } from '../../../session/SessionProvider.js';

/** Every "apply merit to payroll" query sits under this key, so one invalidation refreshes both
 * the status and the settings (same convention as `TRAVEL_QUERY_KEY`). */
export const MERIT_PAYROLL_SETTINGS_QUERY_KEY = ['payroll', 'merit-payroll-settings'] as const;

/** Invalidate after the settings page saves, so the nav entry and the queue page notice at once. */
export function useInvalidateMeritPayrollSettings() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MERIT_PAYROLL_SETTINGS_QUERY_KEY });
}

export interface MeritPayrollModuleStatusResult {
  /** True only once the server has said the switch is on for this organisation. */
  enabled: boolean;
  /** True until the first answer arrives. While loading, `enabled` is false: render a skeleton,
   *  not the "not enabled" message. */
  isLoading: boolean;
}

/**
 * Is "apply merit to payroll" switched on for the signed-in user's organisation (plan §27)?
 *
 * Reads `GET /payroll/merit-settings/status` (`payrollMeritSettings.getModuleStatus`), the one route
 * in that router with no permission and no module guard — any signed-in user may ask, and it never
 * creates data. Use it to gate the "Pending Merit Revisions" nav entry; `MeritPayrollModuleGate`
 * wraps it for the whole screen.
 *
 * Fail closed: a failed request (network, 401) reads as `enabled: false` with `isLoading: false`,
 * same as `useTravelModuleStatus`. Cached for five minutes, since the admin flips the switch rarely;
 * the settings page calls `useInvalidateMeritPayrollSettings()` after saving so the nav updates at
 * once.
 */
export function useMeritPayrollModuleStatus(): MeritPayrollModuleStatusResult {
  const { me } = useAuthenticatedSession();
  // The route has no permission, but the payroll router still sits behind the `statutory_payroll`
  // module gate, so a company without payroll gets a 403. Do not ask; merit is simply off for it.
  // `enabledModules` absent means the session did not say, so the server decides.
  const payrollEntitled = me.enabledModules === undefined || me.enabledModules.includes('statutory_payroll');
  const query = tsr.payrollMeritSettings.getModuleStatus.useQuery({
    queryKey: [...MERIT_PAYROLL_SETTINGS_QUERY_KEY, 'module-status'],
    staleTime: 5 * 60_000,
    enabled: payrollEntitled,
  });
  const body = query.data?.status === 200 ? query.data.body : undefined;
  return {
    enabled: payrollEntitled && body?.enabled === true,
    isLoading: payrollEntitled && query.isLoading,
  };
}
